import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Spin, Tabs } from 'antd';
import {
  FolderOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
  GlobalOutlined,
  ExclamationCircleFilled,
  ClockCircleFilled,
  CheckCircleFilled,
} from '@ant-design/icons';
import axios from 'axios';
import { getProjects } from '../../view/project/ProjectAction';

const { TabPane } = Tabs;

/* Brand colours */
const TEAL    = '#2a5f5a';
const TEAL_10 = 'rgba(42,95,90,0.10)';

/* Urgency tier configuration — matches international teleradiology SLA standards */
const URGENCY_OPTIONS = [
  {
    value: 'STAT',
    label: 'STAT',
    sublabel: '≤ 30 minutes',
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fecaca',
    icon: <ExclamationCircleFilled style={{ color: '#dc2626', fontSize: 18 }} />,
  },
  {
    value: 'URGENT',
    label: 'Urgent',
    sublabel: '≤ 2 hours',
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
    icon: <ClockCircleFilled style={{ color: '#d97706', fontSize: 18 }} />,
  },
  {
    value: 'ROUTINE',
    label: 'Routine',
    sublabel: '≤ 24 hours',
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    icon: <CheckCircleFilled style={{ color: '#16a34a', fontSize: 18 }} />,
  },
];

export function ShareStudyDialog({ study, open, onOpenChange, onSuccess }) {
  /* ── "Add to Project" state ── */
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects]               = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [uploading, setUploading]             = useState(false);

  /* ── "Send to Teleradiology" state ── */
  const [urgency, setUrgency]               = useState('ROUTINE');
  const [clinicalNotes, setClinicalNotes]   = useState('');
  const [submittingTele, setSubmittingTele] = useState(false);

  /* ── Active tab ── */
  const [activeTab, setActiveTab] = useState('project');

  /* Reset state on every open */
  useEffect(() => {
    if (open) {
      setSelectedProject(null);
      setUploading(false);
      setProjects([]);
      setUrgency('ROUTINE');
      setClinicalNotes('');
      setSubmittingTele(false);
      setActiveTab('project');
      loadProjectList();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectList = async () => {
    setLoadingProjects(true);
    try {
      const { data } = await getProjects({ _offset: 0, _limit: 100, role: ['PROJECT_OWNER'] });
      const list = (data?.data || []).map((p) => {
        let percent = 0;
        try {
          const meta      = p.meta || {};
          const total     = Object.values(meta).reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0);
          const completed = meta.COMPLETED || 0;
          percent = total > 0 ? Math.round((completed / total) * 100) : 0;
        } catch (_) {}
        return { id: p.id, name: p.name, completion: percent };
      });
      setProjects(list);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  /* ── Add to Project ── */
  const handleAddToProject = async () => {
    if (!study || !selectedProject) {
      message.error('Please select a project');
      return;
    }
    setUploading(true);
    try {
      const projectName = projects.find((p) => p.id === selectedProject)?.name || 'project';
      const formData = new FormData();
      formData.append('orthanc_study_id', study.id);
      formData.append('project_id', selectedProject);

      const resp = await axios.post('/uploader/studies/copy-from-orthanc', formData);

      const studyLabel = study.patientName || 'Study';
      if (resp.status === 202) {
        message.success(`${studyLabel} is being added to ${projectName}. It will appear in your project within seconds.`);
      } else if (resp.status === 207) {
        message.warning(`${studyLabel} partially added to ${projectName} (${resp.data.uploaded}/${resp.data.total} images)`);
      } else {
        message.success(`${studyLabel} added to ${projectName} successfully`);
      }
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to add study to project';
      message.error(msg);
    } finally {
      setUploading(false);
    }
  };

  /* ── Send to Teleradiology ── */
  const handleSendToTeleradiology = async () => {
    if (!study) return;
    setSubmittingTele(true);
    try {
      const teleResp = await axios.post('/api/studies/teleradiology/submit', {
        source_orthanc_id:    study.id,
        study_instance_uid:   study.studyInstanceUID || study.uid || '',
        urgency,
        clinical_notes:       clinicalNotes.trim(),
        modality:             study.modality || '',
        body_part:            study.bodyPart  || '',
        patient_id:           study.patientId || '',
        patient_name:         study.patientName || '',
        study_date:           study.studyDate || '',
        source_hospital_name: study.institutionName || '',
      });

      const studyLabel = study.patientName || 'Study';
      if (teleResp.data?.already_submitted) {
        message.warning(`${studyLabel} has already been sent for teleradiology reading.`);
        if (onSuccess) onSuccess();   // refresh so the "Assigned To" column reflects tele status
        onOpenChange(false);
        return;
      }
      const urgencyOption = URGENCY_OPTIONS.find(o => o.value === urgency);
      message.success(
        `${studyLabel} sent for teleradiology reading — ${urgencyOption?.label} (${urgencyOption?.sublabel})`
      );
      if (onSuccess) onSuccess();     // refresh the studies list → study now shows as sent to teleradiology
      onOpenChange(false);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send study for teleradiology';
      message.error(errMsg);
    } finally {
      setSubmittingTele(false);
    }
  };

  if (!study) return null;

  /* ── Study info chip (shared across both tabs) ── */
  const StudyChip = () => (
    <div
      style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 12,
        marginBottom: 20,
        marginTop: 4,
        fontSize: 14,
      }}
    >
      <span style={{ color: '#4b5563' }}>Study: </span>
      <span style={{ color: '#111827', fontWeight: 500 }}>
        {study.patientName} — {study.modality} {study.bodyPart || ''}
      </span>
    </div>
  );

  /* ── Project row ── */
  const ProjectRow = ({ project, isLast }) => {
    const selected = selectedProject === project.id;
    return (
      <button
        type="button"
        onClick={() => setSelectedProject(project.id)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          border: 'none',
          borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
          borderLeft: `4px solid ${selected ? TEAL : 'transparent'}`,
          background: selected ? TEAL_10 : '#ffffff',
          cursor: 'pointer',
          transition: 'background 0.15s',
          outline: 'none',
          textAlign: 'left',
        }}
        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = '#f9fafb'; }}
        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = selected ? TEAL_10 : '#ffffff'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
          <div
            style={{
              width: 16, height: 16, borderRadius: '50%',
              border: `2px solid ${selected ? TEAL : '#d1d5db'}`,
              background: selected ? TEAL : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ffffff' }} />}
          </div>
          <span style={{ fontSize: 14, fontWeight: 500, color: selected ? TEAL : '#111827' }}>
            {project.name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{ width: 80, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${project.completion}%`, background: TEAL, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#4b5563', width: 40, textAlign: 'right' }}>
            {project.completion}%
          </span>
        </div>
      </button>
    );
  };

  /* ── Urgency card ── */
  const UrgencyCard = ({ option }) => {
    const selected = urgency === option.value;
    return (
      <button
        type="button"
        onClick={() => setUrgency(option.value)}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          padding: '14px 8px',
          border: `2px solid ${selected ? option.color : '#e5e7eb'}`,
          borderRadius: 10,
          background: selected ? option.bg : '#ffffff',
          cursor: 'pointer',
          transition: 'all 0.15s',
          outline: 'none',
        }}
      >
        {option.icon}
        <span style={{ fontSize: 13, fontWeight: 700, color: selected ? option.color : '#374151' }}>
          {option.label}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>{option.sublabel}</span>
      </button>
    );
  };

  /* ── Footer buttons per tab ── */
  const projectFooter = [
    <Button key="cancel" onClick={() => onOpenChange(false)} disabled={uploading} style={{ borderColor: '#d1d5db', color: '#374151' }}>
      Cancel
    </Button>,
    <Button
      key="add"
      type="primary"
      disabled={!selectedProject || uploading}
      loading={uploading}
      style={{ background: selectedProject && !uploading ? TEAL : undefined, borderColor: selectedProject && !uploading ? TEAL : undefined }}
      onClick={handleAddToProject}
    >
      {uploading ? 'Uploading...' : <><span>Add to Project</span><ArrowRightOutlined style={{ marginLeft: 6 }} /></>}
    </Button>,
  ];

  const teleFooter = [
    <Button key="cancel" onClick={() => onOpenChange(false)} disabled={submittingTele} style={{ borderColor: '#d1d5db', color: '#374151' }}>
      Cancel
    </Button>,
    <Button
      key="send"
      type="primary"
      loading={submittingTele}
      style={{ background: '#1d4ed8', borderColor: '#1d4ed8' }}
      onClick={handleSendToTeleradiology}
    >
      {submittingTele ? 'Sending...' : <><GlobalOutlined style={{ marginRight: 6 }} />Send for Reading</>}
    </Button>,
  ];

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {activeTab === 'project'
            ? <FolderOutlined style={{ color: TEAL, fontSize: 18 }} />
            : <GlobalOutlined style={{ color: '#1d4ed8', fontSize: 18 }} />}
          {activeTab === 'project' ? 'Add to Project' : 'Send to Teleradiology'}
        </span>
      }
      visible={open}
      onCancel={() => !(uploading || submittingTele) && onOpenChange(false)}
      width={540}
      maskClosable={!(uploading || submittingTele)}
      wrapClassName="share-study-modal"
      footer={activeTab === 'project' ? projectFooter : teleFooter}
    >
      <StudyChip />

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: -8 }}>

        {/* ── Tab 1: Add to Project (unchanged behaviour) ── */}
        <TabPane tab={<span><FolderOutlined />Project</span>} key="project">
          {uploading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0 8px' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: TEAL }} spin />} />
              <span style={{ fontSize: 14, color: '#374151' }}>Uploading to project...</span>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: '#374151', fontWeight: 500 }}>
                Select a project
              </div>
              {loadingProjects ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: TEAL }} spin />} />
                </div>
              ) : projects.length === 0 ? (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                  No projects available
                </div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
                  {projects.map((project, idx) => (
                    <ProjectRow key={project.id} project={project} isLast={idx === projects.length - 1} />
                  ))}
                </div>
              )}
            </>
          )}
        </TabPane>

        {/* ── Tab 2: Send to Teleradiology ── */}
        <TabPane tab={<span><GlobalOutlined />Teleradiology</span>} key="teleradiology">
          {submittingTele ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0 8px' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: '#1d4ed8' }} spin />} />
              <span style={{ fontSize: 14, color: '#374151' }}>Sending study for remote reading...</span>
            </div>
          ) : (
            <>
              {/* Urgency selector */}
              <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Reading Priority
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {URGENCY_OPTIONS.map(opt => <UrgencyCard key={opt.value} option={opt} />)}
              </div>

              {/* SLA reminder */}
              <div style={{
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: 6,
                padding: '8px 12px',
                marginBottom: 20,
                fontSize: 12,
                color: '#0369a1',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <ClockCircleFilled />
                Report guaranteed within{' '}
                <strong>
                  {urgency === 'STAT' ? '30 minutes' : urgency === 'URGENT' ? '2 hours' : '24 hours'}
                </strong>
                {' '}per your service agreement.
              </div>

              {/* Clinical notes */}
              <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>
                Clinical Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
              </div>
              <textarea
                value={clinicalNotes}
                onChange={(e) => setClinicalNotes(e.target.value)}
                placeholder="Clinical history, relevant symptoms, specific question for the radiologist…"
                rows={4}
                maxLength={1000}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#111827',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                {clinicalNotes.length}/1000
              </div>

              {/* Disclaimer */}
              <div style={{ marginTop: 16, fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                By submitting, a copy of this study will be transferred to the AfyaNX teleradiology
                service for remote reading. The original study remains in your PACS. Completed
                reports will be delivered to your dashboard.
              </div>
            </>
          )}
        </TabPane>

      </Tabs>
    </Modal>
  );
}

export default ShareStudyDialog;
